import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getAdminCatalogSystems,
  resolveAdminCatalogSystem,
  getAdminCatalogSystemMedia,
  getGroupById,
  getGroupedProducts,
} from '../../api';
import type { ProductGroup, ProductGroupMediaSlide } from '../../types';
import { resolveProductGroupFullCopy, resolveProductGroupShortCopy } from '../../lib/productGroupCopy';
import {
  catalogSystemsForTier,
  isRazdvizhnyeBranch,
  normalizeCatalogTierSlug,
} from '../../lib/razdvizhnyeTier';

function normalizeCatalogTitle(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

type CatalogListRow = {
  id: number;
  slug?: string;
  title?: string;
  short_description?: string;
  subcategory?: string;
};

interface UseAdminCatalogMatchResult {
  adminSystemId: number | null;
  adminCatalogSlug: string | null;
  publicV2Slug: string | null;
  adminGalleryList: ProductGroupMediaSlide[] | null;
  adminDescDraft: string;
  adminFullDescDraft: string;
  adminAdvantagesDraft: string;
  adminEditingDesc: boolean;
  adminEditingFullDesc: boolean;
  adminEditingAdvantages: boolean;
  adminSavingDesc: boolean;
  adminSavingFullDesc: boolean;
  adminSavingAdvantages: boolean;
  setAdminGalleryList: React.Dispatch<React.SetStateAction<ProductGroupMediaSlide[] | null>>;
  setAdminDescDraft: React.Dispatch<React.SetStateAction<string>>;
  setAdminFullDescDraft: React.Dispatch<React.SetStateAction<string>>;
  setAdminAdvantagesDraft: React.Dispatch<React.SetStateAction<string>>;
  setAdminEditingDesc: React.Dispatch<React.SetStateAction<boolean>>;
  setAdminEditingFullDesc: React.Dispatch<React.SetStateAction<boolean>>;
  setAdminEditingAdvantages: React.Dispatch<React.SetStateAction<boolean>>;
  setAdminSavingDesc: React.Dispatch<React.SetStateAction<boolean>>;
  setAdminSavingFullDesc: React.Dispatch<React.SetStateAction<boolean>>;
  setAdminSavingAdvantages: React.Dispatch<React.SetStateAction<boolean>>;
  reloadAdminGallery: () => Promise<void>;
  setGroup: React.Dispatch<React.SetStateAction<ProductGroup | null>>;
}

/** `routeSlug`: v2:: slug, голый slug системы или ключ витрины branch::subtype (его нельзя кормить /admin/catalog-systems как slug). */
export function useAdminCatalogMatch(
  group: ProductGroup | null,
  isAdmin: boolean,
  routeGroupId: string | null,
  routeSlug: string | null,
  effectiveCatalogSlug: string | null,
  setGroup: React.Dispatch<React.SetStateAction<ProductGroup | null>>,
  catalogTier?: string | null,
): UseAdminCatalogMatchResult {
  const [adminSystemId, setAdminSystemId] = useState<number | null>(null);
  const [adminCatalogSlug, setAdminCatalogSlug] = useState<string | null>(null);
  const [publicV2Slug, setPublicV2Slug] = useState<string | null>(null);
  const [adminGalleryList, setAdminGalleryList] = useState<ProductGroupMediaSlide[] | null>(null);
  const [adminDescDraft, setAdminDescDraft] = useState('');
  const [adminFullDescDraft, setAdminFullDescDraft] = useState('');
  const [adminAdvantagesDraft, setAdminAdvantagesDraft] = useState('');
  const [adminEditingDesc, setAdminEditingDesc] = useState(false);
  const [adminEditingFullDesc, setAdminEditingFullDesc] = useState(false);
  const [adminEditingAdvantages, setAdminEditingAdvantages] = useState(false);
  const [adminSavingDesc, setAdminSavingDesc] = useState(false);
  const [adminSavingFullDesc, setAdminSavingFullDesc] = useState(false);
  const [adminSavingAdvantages, setAdminSavingAdvantages] = useState(false);

  const adminCatalogMatchGen = useRef(0);
  const adminEditingRef = useRef(false);
  const adminEditingAny =
    adminEditingDesc || adminEditingFullDesc || adminEditingAdvantages;

  useEffect(() => {
    adminEditingRef.current = adminEditingAny;
  }, [adminEditingAny]);

  useEffect(() => {
    setAdminGalleryList(null);
  }, [catalogTier]);

  const syncDraftsFromGroup = useCallback(
    (g: ProductGroup | null) => {
      if (!g) return;
      setAdminDescDraft(resolveProductGroupShortCopy(g) || String(g.short_description || ''));
      setAdminFullDescDraft(resolveProductGroupFullCopy(g));
      setAdminAdvantagesDraft(Array.isArray(g.advantages) ? g.advantages.join('\n') : '');
    },
    [],
  );

  const isBranchSubtypeRouteKey = Boolean(
    routeSlug &&
      routeSlug.includes('::') &&
      !routeSlug.startsWith('v2::') &&
      !routeSlug.startsWith('st::'),
  );

  const catalogSlugFromGroup = group?.catalog_v2_slug != null && String(group.catalog_v2_slug).trim() !== ''
    ? String(group.catalog_v2_slug).trim()
    : null;

  const systemSlugForMatch = isBranchSubtypeRouteKey
    ? catalogSlugFromGroup
    : routeSlug && String(routeSlug).trim() !== ''
      ? String(routeSlug).trim()
      : null;

  const adminLookupKey = systemSlugForMatch ?? (group?.name?.trim() || null);

  // Admin catalog system match
  useEffect(() => {
    if (!isAdmin || !adminLookupKey) {
      setAdminSystemId(null);
      setAdminCatalogSlug(null);
      setAdminEditingDesc(false);
      setAdminEditingFullDesc(false);
      setAdminEditingAdvantages(false);
      setAdminDescDraft('');
      setAdminFullDescDraft('');
      setAdminAdvantagesDraft('');
      return;
    }
    const gen = ++adminCatalogMatchGen.current;
    getAdminCatalogSystems({ search: adminLookupKey, category: group?.category || undefined, limit: 200 })
      .then((res) => {
        if (gen !== adminCatalogMatchGen.current) return;
        const list: CatalogListRow[] = res?.items || [];
        const tierNorm = normalizeCatalogTierSlug(catalogTier);
        const tierSplit = Boolean(tierNorm && group && isRazdvizhnyeBranch(group));
        const pool = tierSplit ? catalogSystemsForTier(list, tierNorm) : list;
        const normalizedName = normalizeCatalogTitle(String(group?.name || ''));
        const exactBySlug = systemSlugForMatch
          ? pool.find((x) => String(x?.slug || '') === systemSlugForMatch)
            ?? list.find((x) => String(x?.slug || '') === systemSlugForMatch)
          : null;
        const exactByTitle =
          tierSplit || !normalizedName
            ? null
            : pool.find((x) => {
                if (normalizeCatalogTitle(String(x?.title || '')) !== normalizedName) return false;
                if (group?.subcategory && String(x.subcategory || '') !== String(group.subcategory)) return false;
                return true;
              }) || pool.find((x) => normalizeCatalogTitle(String(x?.title || '')) === normalizedName);
        const exact = exactBySlug || exactByTitle || null;
        if (!exact) {
          if (tierSplit && !systemSlugForMatch) {
            setAdminSystemId(null);
            setAdminCatalogSlug(null);
            return;
          }
          if (!group?.name) { setAdminSystemId(null); setAdminCatalogSlug(null); setAdminDescDraft(''); return; }
          resolveAdminCatalogSystem({
            slug: systemSlugForMatch || undefined,
            title: group.name,
            category: group.category,
            subcategory: group.subcategory,
            short_description: resolveProductGroupShortCopy(group) || group.short_description || '',
            image_url: group.image_url || '',
            product_group_id: group.group_id,
          })
            .then((created) => {
              if (gen !== adminCatalogMatchGen.current) return;
              const createdId = Number(created?.id ?? 0);
              if (!createdId) { setAdminSystemId(null); setAdminCatalogSlug(null); return; }
              setAdminSystemId(createdId);
              setAdminCatalogSlug(String(created?.slug || '').trim() || null);
              if (!adminEditingRef.current) syncDraftsFromGroup(group);
            })
            .catch(() => { if (gen !== adminCatalogMatchGen.current) return; setAdminSystemId(null); setAdminCatalogSlug(null); });
          return;
        }
        if (gen !== adminCatalogMatchGen.current) return;
        setAdminSystemId(Number(exact.id));
        setAdminCatalogSlug(String(exact.slug || '').trim() || null);
        if (group && !adminEditingRef.current) {
          syncDraftsFromGroup(group);
        }
        if (group?.group_id && String(exact.slug || '').trim()) {
          void resolveAdminCatalogSystem({
            slug: String(exact.slug),
            title: group.name,
            category: group.category,
            subcategory: group.subcategory,
            short_description: String(group.short_description || ''),
            image_url: String(group.image_url || ''),
            product_group_id: group.group_id,
          }).catch(() => {});
        }
      })
      .catch(() => { if (gen !== adminCatalogMatchGen.current) return; setAdminSystemId(null); setAdminCatalogSlug(null); });
  }, [
    isAdmin,
    adminLookupKey,
    systemSlugForMatch,
    routeSlug,
    catalogSlugFromGroup,
    group?.group_id,
    group?.name,
    group?.category,
    group?.subcategory,
    catalogTier,
    syncDraftsFromGroup,
  ]);

  useEffect(() => {
    if (!isAdmin || !group || adminEditingAny) return;
    syncDraftsFromGroup(group);
  }, [
    isAdmin,
    group?.group_id,
    catalogTier,
    group?.short_description,
    group?.short_summary,
    group?.marketing_description,
    group?.description,
    group?.advantages,
    adminEditingAny,
    syncDraftsFromGroup,
  ]);

  // Enrich legacy group with v2 gallery (не для раздвижных tier-карточек — там только API ?tier=)
  useEffect(() => {
    if (!routeGroupId || routeGroupId.startsWith('v2::')) return;
    if (group && isRazdvizhnyeBranch(group) && normalizeCatalogTierSlug(catalogTier)) return;
    const slug = effectiveCatalogSlug?.trim();
    if (!slug || !group) return;
    let cancelled = false;
    getGroupById(`v2::${slug}`)
      .then((v2) => {
        if (cancelled) return;
        setGroup((prev) => {
          if (!prev) return prev;
          const fromGroup = Array.isArray(prev.gallery) ? prev.gallery : [];
          const fromV2 = Array.isArray(v2?.gallery) ? v2.gallery : [];
          return { ...prev, gallery: fromV2.length > 0 ? fromV2 : fromGroup, image_url: v2.image_url || prev.image_url };
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [routeGroupId, effectiveCatalogSlug, group?.group_id, group?.category, group?.branch_slug, catalogTier]);

  // Public v2 slug for guests on legacy URLs
  useEffect(() => {
    if (!group || !routeGroupId || routeGroupId.startsWith('v2::')) return;
    if (adminCatalogSlug || routeSlug) return;
    const cat = String(group.category || '').trim();
    if (!cat) return;
    let cancelled = false;
    getGroupedProducts({ category: cat })
      .then((rows: any[]) => {
        if (cancelled || !Array.isArray(rows)) return;
        const wantName = normalizeCatalogTitle(String(group.name || ''));
        const wantSub = normalizeCatalogTitle(String(group.subcategory || ''));
        let hit = rows.find((row: any) => {
          if (!String(row?.group_id || '').startsWith('v2::')) return false;
          return normalizeCatalogTitle(String(row?.name || '')) === wantName &&
                 normalizeCatalogTitle(String(row?.subcategory || '')) === wantSub;
        }) || rows.find((row: any) => {
          if (!String(row?.group_id || '').startsWith('v2::')) return false;
          return normalizeCatalogTitle(String(row?.name || '')) === wantName;
        });
        if (!hit) return;
        const slug = String(hit.group_id || '').replace(/^v2::/, '').trim();
        if (slug) setPublicV2Slug(slug);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [group, routeGroupId, adminCatalogSlug, routeSlug]);

  // Load admin gallery
  useEffect(() => {
    if (!isAdmin || !adminSystemId) { setAdminGalleryList(null); return; }
    let cancelled = false;
    getAdminCatalogSystemMedia(adminSystemId)
      .then((rows) => { if (!cancelled) setAdminGalleryList(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setAdminGalleryList(null); });
    return () => { cancelled = true; };
  }, [isAdmin, adminSystemId]);

  const reloadAdminGallery = useCallback(async () => {
    if (!adminSystemId) return;
    try {
      const rows = await getAdminCatalogSystemMedia(adminSystemId);
      setAdminGalleryList(Array.isArray(rows) ? rows : []);
    } catch {
      setAdminGalleryList(null);
    }
  }, [adminSystemId]);

  return {
    adminSystemId, adminCatalogSlug, publicV2Slug,
    adminGalleryList, setAdminGalleryList,
    adminDescDraft, adminFullDescDraft, adminAdvantagesDraft,
    adminEditingDesc, adminEditingFullDesc, adminEditingAdvantages,
    adminSavingDesc, adminSavingFullDesc, adminSavingAdvantages,
    setAdminDescDraft, setAdminFullDescDraft, setAdminAdvantagesDraft,
    setAdminEditingDesc, setAdminEditingFullDesc, setAdminEditingAdvantages,
    setAdminSavingDesc, setAdminSavingFullDesc, setAdminSavingAdvantages,
    reloadAdminGallery,
    setGroup,
  };
}
